import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { apiFetch } from './api';

export const STATES = ['Andhra Pradesh', 'Delhi', 'Gujarat', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'West Bengal'];

export function calculateDraftInvoice(form) {
  const items = (form.items || []).map((item, index) => {
    const quantity = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;
    return { ...item, srNo: index + 1, quantity, rate, amount: Math.round(quantity * rate * 100) / 100 };
  });
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const discount = Number(form.discount) || 0;
  const otherCharges = Number(form.otherCharges) || 0;
  const taxableAmount = Math.max(0, subtotal - discount + otherCharges);
  const totalTax = taxableAmount * (Number(form.taxRate) || 0) / 100;
  const cgst = form.supplyType === 'Inter-State' ? 0 : totalTax / 2;
  const sgst = form.supplyType === 'Inter-State' ? 0 : totalTax / 2;
  const igst = form.supplyType === 'Inter-State' ? totalTax : 0;
  const calculatedTotal = taxableAmount + totalTax;
  const roundOff = form.roundOffEnabled ? Math.round(calculatedTotal) - calculatedTotal : 0;
  const grandTotal = calculatedTotal + roundOff;
  return { items, subtotal, discount, otherCharges, taxableAmount, totalTax, cgst, sgst, igst, roundOff, grandTotal };
}

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function wordsBelowHundred(value) {
  if (value < 10) return ones[value];
  if (value < 20) return teens[value - 10];
  return `${tens[Math.floor(value / 10)]}${value % 10 ? `-${ones[value % 10]}` : ''}`;
}

function numberToWords(value) {
  const number = Math.floor(Number(value) || 0);
  if (!number) return 'Zero';
  const parts = [];
  const crore = Math.floor(number / 10000000);
  const lakh = Math.floor((number % 10000000) / 100000);
  const thousand = Math.floor((number % 100000) / 1000);
  const remainder = number % 1000;
  if (crore) parts.push(`${numberToWords(crore)} Crore`);
  if (lakh) parts.push(`${numberToWords(lakh)} Lakh`);
  if (thousand) parts.push(`${numberToWords(thousand)} Thousand`);
  if (remainder >= 100) parts.push(`${ones[Math.floor(remainder / 100)]} Hundred`);
  if (remainder % 100) parts.push(wordsBelowHundred(remainder % 100));
  return parts.join(' ');
}

export function amountInWords(value) {
  const rupees = Math.floor(Number(value) || 0);
  const paise = Math.round(((Number(value) || 0) - rupees) * 100);
  return `${numberToWords(rupees)} Rupees${paise ? ` and ${numberToWords(paise)} Paise` : ''} Only`;
}

export function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function sanitizeFilename(value) {
  return String(value || 'Customer').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'Customer';
}

function pdfText(value) { return String(value || '-'); }

function loadStampImage() {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext('2d').drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = reject;
    image.src = '/quick-loanz-stamp.png';
  });
}

async function downloadInvoicePdfFallback(invoice) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const left = 14;
  const right = 196;
  const contentWidth = right - left;
  let y = 16;
  const currency = (value) => `INR ${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const line = () => { pdf.line(left, y, right, y); y += 5; };
  const wrapped = (value, x, width, size = 8.5) => {
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(pdfText(value), width);
    pdf.text(lines, x, y);
    return Math.max(1, lines.length) * 4;
  };
  const summaryRow = (label, value) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.text(label, 116, y);
    pdf.text(currency(value), right, y, { align: 'right' });
    y += 5;
  };

  pdf.setDrawColor(35, 47, 62);
  pdf.setTextColor(35, 47, 62);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(27, 156, 156);
  pdf.text(pdfText(invoice.vendor?.name || 'Service Provider').toUpperCase(), left, y);
  pdf.setTextColor(35, 47, 62);
  y += 6;
  pdf.setFontSize(18);
  pdf.text('TAX INVOICE', left, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.text(`Invoice No: ${pdfText(invoice.invoiceNumber)}`, right, y - 5, { align: 'right' });
  pdf.text(`Invoice Date: ${invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('en-IN') : '-'}`, right, y, { align: 'right' });
  pdf.text(`Place of Supply: ${pdfText(invoice.placeOfSupply)}`, right, y + 5, { align: 'right' });
  y += 10; line();

  const partyTop = y;
  const partyWidth = 86;
  const partyGap = 10;
  const renderParty = (title, party, x) => {
    let partyY = partyTop;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text(title, x, partyY); partyY += 6;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); partyY += wrapped(party.name, x, partyWidth, 9);
    pdf.setFont('helvetica', 'normal');
    partyY += wrapped([party.addressLine1, party.addressLine2, party.address].filter(Boolean).join(', '), x, partyWidth);
    partyY += wrapped([party.area, party.city, party.state, party.pincode].filter(Boolean).join(', '), x, partyWidth);
    partyY += wrapped(`Phone: ${party.phone || party.mobile || '-'}`, x, partyWidth);
    partyY += wrapped(`Email: ${party.email || '-'}`, x, partyWidth);
    partyY += wrapped(`PAN: ${party.pan || '-'}`, x, partyWidth);
    partyY += wrapped(`GSTIN: ${party.gstin || '-'}`, x, partyWidth);
    return partyY;
  };
  const vendorBottom = renderParty('VENDOR', invoice.vendor || {}, left);
  const billToBottom = renderParty('BILL TO', invoice.billTo || {}, left + partyWidth + partyGap);
  y = Math.max(vendorBottom, billToBottom) + 3;
  line(); y += 3;

  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text('SERVICE DETAILS', left, y); y += 5;
  const tableX = [left, 25, 94, 125, 143, 168, right];
  const tableWidths = [11, 69, 31, 18, 25, 28];
  pdf.setFillColor(35, 47, 62); pdf.setTextColor(255, 255, 255); pdf.rect(left, y - 4, contentWidth, 7, 'F'); pdf.setFontSize(7.5);
  ['Sr', 'Description', 'HSN/SAC', 'Qty', 'Rate', 'Amount'].forEach((header, index) => pdf.text(header, tableX[index] + 2, y));
  y += 7; pdf.setTextColor(35, 47, 62); pdf.setFont('helvetica', 'normal');
  (invoice.items || []).forEach((item) => {
    const descriptionLines = pdf.splitTextToSize(pdfText(item.description), tableWidths[1] - 4);
    const rowHeight = Math.max(6, descriptionLines.length * 4);
    pdf.text(String(item.srNo), tableX[0] + 2, y);
    pdf.text(descriptionLines, tableX[1] + 2, y);
    pdf.text(pdfText(item.hsnSac), tableX[2] + 2, y);
    pdf.text(String(item.quantity), tableX[3] + 2, y);
    pdf.text(currency(item.rate), tableX[4] + tableWidths[4] - 2, y, { align: 'right' });
    pdf.text(currency(item.amount), right, y, { align: 'right' });
    y += rowHeight; pdf.setDrawColor(215, 220, 229); pdf.line(left, y - 2, right, y - 2);
  });
  y += 4;
  summaryRow('Subtotal', invoice.subtotal); summaryRow('Taxable Amount', invoice.taxableAmount);
  if (invoice.supplyType === 'Inter-State') summaryRow(`IGST ${invoice.taxRate}%`, invoice.igst);
  else { summaryRow(`CGST ${invoice.taxRate / 2}%`, invoice.cgst); summaryRow(`SGST ${invoice.taxRate / 2}%`, invoice.sgst); }
  summaryRow('Other Charges', invoice.otherCharges); summaryRow('Discount', invoice.discount); summaryRow('Round Off', invoice.roundOff);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); pdf.text('INVOICE TOTAL', left, y); pdf.text(currency(invoice.grandTotal), right, y, { align: 'right' }); y += 7;
  pdf.setFontSize(8.5); pdf.text(`Amount in Words: ${pdfText(invoice.amountInWords || amountInWords(invoice.grandTotal))}`, left, y); y += 7; line(); y += 2;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text('REMITTANCE', left, y); y += 5; pdf.setFont('helvetica', 'normal'); pdf.text(`Bank Name: ${pdfText(invoice.bankDetails?.bankName)}`, left, y); pdf.text(`Account Number: ${pdfText(invoice.bankDetails?.accountNumber)}`, 80, y); y += 5; pdf.text(`IFSC: ${pdfText(invoice.bankDetails?.ifsc)}`, left, y); y += 7; line(); y += 2;
  pdf.setFont('helvetica', 'bold'); pdf.text('TERMS AND CONDITIONS', left, y); y += 5; pdf.setFont('helvetica', 'normal'); const terms = pdf.splitTextToSize(pdfText(invoice.termsAndConditions), 105); pdf.text(terms, left, y); const signatureY = y + Math.max(terms.length * 4, 12);
  pdf.addImage(await loadStampImage(), 'PNG', right - 58, signatureY - 22, 58, 38.9);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5); pdf.text('For Quick Loanz', right, signatureY + 22, { align: 'right' }); pdf.line(right - 58, signatureY + 24, right, signatureY + 24); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.text('Authorized Signatory', right, signatureY + 29, { align: 'right' }); pdf.setFont('helvetica', 'bold'); pdf.text('Proprietor', right, signatureY + 34, { align: 'right' });
  pdf.setFontSize(8); pdf.setTextColor(110, 110, 110); pdf.text('Generated from MyLoanCRM Billing', left, 288); pdf.text('Page 1 of 1', right, 288, { align: 'right' });
  pdf.save(`Invoice_${invoice.invoiceNumber}_${sanitizeFilename(invoice.billTo?.name)}.pdf`);
}

export async function downloadInvoicePdf(invoice, previewElement) {
  if (!previewElement) {
    await downloadInvoicePdfFallback(invoice);
    return;
  }

  const canvas = await html2canvas(previewElement, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    logging: false,
  });
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 8;
  const imageWidth = pageWidth - (margin * 2);
  const imageHeight = (canvas.height * imageWidth) / canvas.width;
  const imageData = canvas.toDataURL('image/jpeg', 0.96);
  let remainingHeight = imageHeight;
  let offset = 0;

  while (remainingHeight > 0) {
    pdf.addImage(imageData, 'JPEG', margin, margin - offset, imageWidth, imageHeight, undefined, 'FAST');
    remainingHeight -= pageHeight - (margin * 2);
    offset += pageHeight - (margin * 2);
    if (remainingHeight > 0) pdf.addPage();
  }

  pdf.save(`Invoice_${invoice.invoiceNumber}_${sanitizeFilename(invoice.billTo?.name)}.pdf`);
}

export async function fetchBillingSettings() { const response = await apiFetch('/api/invoices/settings'); return response?.data || {}; }
export async function fetchBillingCustomers() { const response = await apiFetch('/api/invoices/billing-customers'); return response?.data || []; }
export async function fetchInvoices() { const response = await apiFetch('/api/invoices'); return response?.data || []; }
export async function fetchInvoice(id) { const response = await apiFetch(`/api/invoices/${id}`); return response?.data; }
export async function saveInvoice(invoice) { const response = await apiFetch('/api/invoices', { method: 'POST', body: invoice }); return response?.data; }
export async function updateInvoice(id, invoice) { const response = await apiFetch(`/api/invoices/${id}`, { method: 'PUT', body: invoice }); return response?.data; }
export async function cancelInvoice(id) { const response = await apiFetch(`/api/invoices/${id}/cancel`, { method: 'PATCH' }); return response?.data; }
export async function deleteInvoice(id) { const response = await apiFetch(`/api/invoices/${id}`, { method: 'DELETE' }); return response?.data; }